<script>
import router from "../../../../router";
import navBar from "@/layouts/components/navbar/NavBarHorizontalLayout.vue";
import isSigningSimple from "@/views/pages/sign-document/components/isSigningSimple.vue";
import isSigning from "@/views/pages/sign-document/components/isSigningLoading.vue";
import vueSignature from "vue-signature";
import CameraInstructions from "./CameraInstructions.vue";

export default {
  name: "SignatureFlow",
  components: {isSigningSimple, isSigning, navBar, vueSignature, CameraInstructions},
  data() {
    return {
      uuid: null,
      signatureFlowId: null,
      isFinished: false,
      isError: false,
      option: {
        penColor: "rgb(0, 0, 0)",
        backgroundColor: "rgba(255,255,255,0.5)",
      },
      disabled: false,
      isSubmitted: false,
      tries: 1,
      disableRetry: false,
      config: {
        primaryColor: "69,20,199",
        secondaryColor: "90,123,177"
      },
      // Video recording
      mediaRecorder: null,
      cameraStream: null,
      displayStream: null,
      combinedStream: null,
      recordedChunks: [],
      isRecording: false,
      videoBlob: null,
      cameraError: null,
      // Canvas para combinar video
      canvas: null,
      canvasContext: null,
      // UI state
      showCameraControls: false,
      // Modal de instrucciones de cámara
      showCameraModal: false,
      // Mostrar texto "Firma aquí"
      showSignHereText: true,
      // Nueva configuración para controlar si se requiere video
      requiresVideoRecording: false,
      signatureFlowConfig: null
    }
  },
  computed: {
    onMobile() {
      return window.innerWidth < 768
    },
    componentStyle() {
      return {
        "--vs-primary": this.config.primaryColor,
        "--vs-secondary": this.config.secondaryColor
      };
    },
    hasCameraAccess() {
      return this.cameraStream !== null;
    },
    currentBrowser() {
      return this.detectBrowser();
    },
    isMobileDevice() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }
  },
  methods: {
    detectBrowser() {
      const userAgent = navigator.userAgent.toLowerCase();
      if (userAgent.indexOf('edge') > -1) return 'edge';
      if (userAgent.indexOf('chrome') > -1 && userAgent.indexOf('edg') === -1) return 'chrome';
      if (userAgent.indexOf('firefox') > -1) return 'firefox';
      if (userAgent.indexOf('safari') > -1 && userAgent.indexOf('chrome') === -1) return 'safari';
      return 'other';
    },
    retryVerification() {
      this.disableRetry = true;
      this.$store.dispatch("document/retrySignatureFlow", {signatureId: this.uuid, signatureFlowId: this.signatureFlowId})
        .then(
          result => {
            window.location.replace(result)
            this.disableRetry = false;
          },
          () => {
            this.disableRetry = false;
          }
        ).catch(() => {
        this.disableRetry = false;
      })
    },
    signMethod(){
      router.push({name: 'page-sign-document', params: {uuid: this.uuid}})
    },
    hideSignHereText() {
      setTimeout(() => {
        this.showSignHereText = false;
      }, 6000);
    },
    async save() {
      this.$store.dispatch("alert/clear", {}, {root:true});
      if (this.$refs.signature.isEmpty()) {
        this.$vs.notify({
          title: 'Verifica el trazo',
          text: 'No se detectó un trazo en el área',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
          position: "bottom-center"
        })
        return;
      }

      // Solo verificar cámara si se requiere grabación de video
      if (this.requiresVideoRecording && !this.hasCameraAccess) {
        this.$vs.dialog({
          type: 'confirm',
          color: 'warning',
          title: 'Acceso a cámara requerido',
          text: 'Para completar el proceso de firma, es necesario verificar su identidad mediante la cámara. ¿Desea continuar sin cámara?',
          acceptText: 'Cerrar',
          cancelText: 'Habilitar cámara',
          accept: async () => {
            await this.processSave();
          },
          cancel: () => {
            this.showCameraModal = true;
          }
        });
        return;
      }

      await this.processSave();
    },

    async processSave() {
      // Solo detener grabación si está activa
      if (this.requiresVideoRecording) {
        await this.stopRecording();
      }

      var svg = this.$refs.signature.save("image/svg+xml");
      const base64 = svg.split(',')[1];
      const decoded = atob(base64);
      const blob = new Blob([decoded], {type: 'image/svg+xml'});

      this.isSubmitted = true;

      const formData = new FormData();
      formData.append('svg', blob);

      // Solo añadir video si se requiere y existe
      if (this.requiresVideoRecording && this.videoBlob) {
        formData.append('video', this.videoBlob, 'signature_video.webm');
      }

      this.$store.dispatch("document/signSignatureFlow", {
        signatureId: this.uuid,
        signatureFlowId: this.signatureFlowId,
        svg: blob,
        video: this.requiresVideoRecording ? this.videoBlob : null
      })
        .then(
          () => {
            router.push({name: 'page-sign-document', params: {uuid: this.uuid}})
            this.isSubmitted = false;
          },
          () => {
            this.isSubmitted = false;
          }
        )
    },

    clear() {
      this.$refs.signature.clear();
    },
    undo() {
      this.$refs.signature.undo();
    },
    hexToRgb(hex) {
      hex = hex.replace(/^#/, '');

      if (hex.length === 3) {
        hex = hex.split('').map(char => char + char).join('');
      }

      if (hex.length !== 6) {
        throw new Error('El valor hexadecimal proporcionado es inválido.');
      }

      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      return `${r},${g},${b}`;
    },

    // Métodos para la grabación integrada de video
    async initializeCameraAndRecording() {
      // Solo inicializar si se requiere video
      if (!this.requiresVideoRecording) {
        console.log('Video recording not required for this signature flow');
        return;
      }

      try {
        this.setupCanvas();

        const cameraConstraints = {
          video: {
            facingMode: "user",
            width: { ideal: 640 },
            height: { ideal: 480 }
          },
          audio: false
        };

        this.cameraStream = await navigator.mediaDevices.getUserMedia(cameraConstraints);

        this.cameraStream.getTracks().forEach(track => {
          track.onended = () => {
            console.log('El usuario ha revocado el permiso de cámara');
            window.location.reload()
          };
        });

        if (this.$refs.cameraBackground) {
          this.$refs.cameraBackground.srcObject = this.cameraStream;
          this.$refs.cameraBackground.onloadedmetadata = () => {
            this.$refs.cameraBackground.play();
          };
        }

        this.startCanvasRecording();
        this.cameraError = null;
      } catch (error) {
        console.error('Error al acceder a la cámara:', error);
        this.cameraError = `No se pudo acceder a la cámara: ${error.message}`;

        this.$vs.notify({
          title: 'Error de cámara',
          text: 'No se pudo acceder a la cámara frontal. Se requiere tener acceso a la cámara para máximizar la integridad del proceso.',
          iconPack: 'feather',
          icon: 'icon-camera-off',
          color: 'warning',
          position: "top-center"
        });

        this.promptCameraPermission();
      }
    },

    promptCameraPermission() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'warning',
        title: 'Se requiere acceso a la cámara',
        text: 'Para continuar con el proceso de firma, es necesario permitir el acceso a su cámara. Esto nos permite verificar su identidad durante el proceso de firma.',
        acceptText: 'Ver instrucciones',
        cancelText: 'Cerrar',
        accept: () => {
          this.showCameraModal = true;
        },
        cancel: () => {
          this.$vs.notify({
            title: 'Advertencia',
            text: 'No es posible continuar sin acceso a la cámara.',
            iconPack: 'feather',
            icon: 'icon-alert-triangle',
            color: 'warning',
            position: "top-center"
          });
        }
      });
    },

    closeCameraInstructions() {
      this.showCameraModal = false;
    },

    reloadPage() {
      window.location.reload();
    },

    setupCanvas() {
      this.canvas = document.createElement('canvas');
      this.canvas.width = 640;
      this.canvas.height = 480;
      this.canvasContext = this.canvas.getContext('2d');
      this.startCanvasDrawing();
    },

    startCanvasDrawing() {
      const drawFrame = () => {
        if (!this.canvasContext || !this.$refs.cameraBackground) return;

        this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);

        if (this.$refs.cameraBackground.videoWidth) {
          this.canvasContext.drawImage(
            this.$refs.cameraBackground,
            0,
            0,
            this.canvas.width,
            this.canvas.height
          );
        }

        const signatureElement = this.$refs.signature.$el.querySelector('canvas');

        if (signatureElement) {
          const signatureWidth = Math.min(this.canvas.width * 0.8, 500);
          const signatureHeight = Math.min(signatureWidth * 0.5, 250);
          const x = (this.canvas.width - signatureWidth) / 2;
          const y = (this.canvas.height - signatureHeight) / 2;

          this.canvasContext.fillStyle = 'rgba(255, 255, 255, 0.5)';
          this.canvasContext.fillRect(x, y, signatureWidth, signatureHeight);

          this.canvasContext.drawImage(
            signatureElement,
            0,
            0,
            signatureElement.width,
            signatureElement.height,
            x,
            y,
            signatureWidth,
            signatureHeight
          );

          this.canvasContext.strokeStyle = 'rgba(0, 0, 0, 0.3)';
          this.canvasContext.lineWidth = 2;
          this.canvasContext.strokeRect(x, y, signatureWidth, signatureHeight);
        }

        this.animationFrame = requestAnimationFrame(drawFrame);
      };

      this.animationFrame = requestAnimationFrame(drawFrame);
    },

    startCanvasRecording() {
      try {
        const canvasStream = this.canvas.captureStream(30);

        const mimeTypes = [
          'video/webm;codecs=vp9',
          'video/webm;codecs=vp8',
          'video/webm',
          'video/mp4'
        ];

        let selectedMimeType = null;
        for (const mimeType of mimeTypes) {
          if (MediaRecorder.isTypeSupported(mimeType)) {
            selectedMimeType = mimeType;
            console.log(`Usando formato de video: ${selectedMimeType}`);
            break;
          }
        }

        if (!selectedMimeType) {
          console.warn('No se encontró un formato de video compatible.');
          this.cameraError = 'No se encontró un formato de video compatible. Asegúrate de que tu navegador soporte WebM o MP4.';
          return;
        }

        const options = {
          mimeType: selectedMimeType,
          videoBitsPerSecond: 1500000
        };

        this.mediaRecorder = new MediaRecorder(canvasStream, options);

        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.recordedChunks.push(event.data);
          }
        };

        this.mediaRecorder.onstop = () => {
          this.videoBlob = new Blob(this.recordedChunks, { type: 'video/webm' });
          this.recordedChunks = [];
          console.log("Grabación completada. Tamaño del video:", Math.round(this.videoBlob.size / 1024), "KB");
        };

        this.mediaRecorder.start(1000);
        this.isRecording = true;
        console.log("Grabación iniciada");
      } catch (error) {
        console.error('Error al iniciar la grabación:', error);
        this.cameraError = `Error al iniciar la grabación: ${error.message}`;
      }
    },

    async stopRecording() {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }

      if (this.mediaRecorder && this.isRecording) {
        return new Promise((resolve) => {
          this.mediaRecorder.onstop = () => {
            this.videoBlob = new Blob(this.recordedChunks, { type: 'video/webm' });
            this.recordedChunks = [];
            this.isRecording = false;
            console.log("Grabación detenida. Tamaño del video:", Math.round(this.videoBlob.size / 1024), "KB");
            resolve();
          };
          this.mediaRecorder.stop();
        });
      } else {
        return Promise.resolve();
      }
    },

    closeCamera() {
      this.stopRecording();

      if (this.cameraStream) {
        this.cameraStream.getTracks().forEach(track => track.stop());
        this.cameraStream = null;
      }

      if (this.canvasContext) {
        this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);
      }
    },

    toggleCameraControls() {
      this.showCameraControls = !this.showCameraControls;
    },

    checkIfVideoRequired(signatureFlowData) {
      console.log(signatureFlowData);
      if (signatureFlowData && signatureFlowData.attributes) {
        if (signatureFlowData.attributes.providerData.provider) {
          return signatureFlowData.attributes.providerData.provider === 'LOCAL';
        }
      }
      // Por defecto, no requerir video
      return false;
    }
  },
  watch: {
    isFinished(newVal) {
      if (newVal === true) {
        this.$nextTick(() => {
          // Solo inicializar cámara si se requiere video
          if (this.requiresVideoRecording) {
            this.initializeCameraAndRecording();
          }
          this.hideSignHereText();
        });
      }
    }
  },
  async created() {
    let uuid = this.$route.params.uuid;
    let signatureFlowId = this.$route.params.signatureFlowId;
    if (uuid && signatureFlowId) {
      this.uuid = uuid;
      let isSigned = false

      await this.$store.dispatch("document/getDocumentFromSignature", {uuid: this.uuid})
        .then(
          document => {
            if (document.attributes.config.primary_color) {
              this.config.primaryColor = this.hexToRgb(document.attributes.config.primary_color);
            }
            if (document.attributes.config.secondary_color) {
              this.config.secondaryColor = this.hexToRgb(document.attributes.config.secondary_color);
            }
            if (
              document.attributes.sign_status === 3
              || document.attributes.sign_status === 4
              || document.attributes.signature_status === 'SIGNED'
            ) {
              isSigned = true
              router.push({name: 'page-sign-document', params: {uuid: this.uuid}})
            }
          },
        )

      if (isSigned) { return }

      const intervalDocumentStatusId = setInterval(() => {
        this.$store.dispatch("document/getDocumentFromSignature", {uuid: this.uuid})
          .then(
            document => {
              if (
                document.attributes.sign_status === 3
                || document.attributes.sign_status === 4
                || document.attributes.signature_status === 'SIGNED'
              ) {
                clearInterval(intervalDocumentStatusId)
                router.push({name: 'page-sign-document', params: {uuid: this.uuid}})
              }
            },
            () => {
              clearInterval(intervalDocumentStatusId)
              router.push({name: 'page-sign-document', params: {uuid: this.uuid}})
            }
          )
      }, 5000);

      this.signatureFlowId = signatureFlowId;
      let triesBeforeRedirect = 0

      const intervalId = setInterval(() => {
        this.$store.dispatch("document/getSignatureFlow", {signatureId: this.uuid, signatureFlowId: this.signatureFlowId})
          .then(
            result => {
              // Guardar la configuración del flujo
              this.signatureFlowConfig = result.data[0];

              // Determinar si se requiere video basado en la configuración
              this.requiresVideoRecording = this.checkIfVideoRequired(result.data[0]);

              this.tries = result.data[0].attributes.try;
              if (result.data[0].attributes.status === "ON_HOLD") {
                if (triesBeforeRedirect >= 3) {
                  window.location.href = result.data[0].attributes.providerData.verificationUrl
                }
                triesBeforeRedirect = triesBeforeRedirect + 1
              }
              if (result.data[0].attributes.status === "COMPLETED") {
                clearInterval(intervalId)
                clearInterval(intervalDocumentStatusId)
                this.isFinished = true;
                if (this.onMobile) {
                  this.$refs.signatureBox.classList.add('signature-box-mobile')
                }
              } else if (result.data[0].attributes.status === "ERROR") {
                clearInterval(intervalId)
                clearInterval(intervalDocumentStatusId)
                this.isError = true;
              }
            }
          )
      }, 5000);
    } else {
      router.push("/");
    }
  },
  beforeDestroy() {
    this.closeCamera();
  }
}
</script>

<template>
  <div :style="componentStyle">
    <transition name="bounce">
      <div v-if="isSubmitted" class="loading-modal">
        <isSigning></isSigning>
      </div>
    </transition>
    <transition name="bounce">
      <div v-if="!isFinished && !isError" class="loading-modal">
        <isSigningSimple></isSigningSimple>
      </div>
    </transition>

    <!-- Componente modal de instrucciones de cámara -->
    <CameraInstructions
      v-if="requiresVideoRecording"
      :isVisible="showCameraModal"
      :browser="currentBrowser"
      :isMobile="isMobileDevice"
      @close="closeCameraInstructions"
      @reload="reloadPage"
    />

    <div class="container">
      <div :style="!isError ? 'display: none;' : ''">
        <navBar/>
        <transition :name="routerTransition">

          <div v-if="$route.meta.breadcrumb || $route.meta.pageTitle" class="router-header flex flex-wrap items-center mb-6">
            <div
              class="content-area__heading"
              :class="{'pr-4 border-0 md:border-r border-solid border-grey-light' : $route.meta.breadcrumb}">
              <h2 class="mb-1 mt-0">Firma autógrafa</h2>
            </div>

            <!-- BREADCRUMB -->
            <vx-breadcrumb class="ml-4 md:block hidden" v-if="$route.meta.breadcrumb" :route="$route" :isRTL="$vs.rtl" />

          </div>
        </transition>
        <div v-if="tries < 3">
          <div class="text-center d-block m-auto">
            <h1 class="text-center mb-5 mt-10">Hubo un problema al validar</h1>
            <p class="d-block m-auto mb-0">Tuvimos un <b>problema con la verificación de tu identidad</b>. No te preocupes, puedes intentarlo una vez más. </p>
            <h2 class="mb-5"><span class="primary">Intentos restantes:</span> <b> {{ 3 - tries }}</b></h2>

          </div>
          <div class="sing-recomendations">
            <h2 class="mb-5 mt-5">Sigue estas recomendaciones:</h2>
            <h3><span class="primary">Paso 1:</span> Ten tu INE a la mano</h3>

            <ul>
              <li>Verifica tu INE <b>esté vigente</b>, asegúrate que no esté dañada y sea legible.</li>
              <li>Debes de estar en <b>lugar bien iluminado</b>.</li>
              <li>Si no cuentas con INE por ser extranjero no podrás firmar.</li>
            </ul>

            <h2><span>Paso 2:</span> Realiza el proceso de verificación</h2>

            <ul>
              <li>Vamos a capturar tu INE, realiza <b>fotos nítidas</b> de la parte frontal y posterior del INE.</li>
              <li><b>Evita tener reflejos</b> y estar a contra luz.</li>
              <li>En la vídeo identificación asegurate que se vea tu rostro claramente.</li>
            </ul>

            <h2><span>Paso 3:</span> Realiza tu trazo de firma</h2>

            <ul>
              <li><b>Terminada la verificación</b> de identidad realiza el trazo de tu firma.</li>

            </ul>
          </div>
          <div class="text-center">
            <vs-button class="mt-5 vs-con-loading__container mb-10 mr-5" color="primary" @click="retryVerification" :disabled="disableRetry">Reintentar</vs-button>
            <vs-button class="mt-5 vs-con-loading__container mb-10" color="secondary" @click="signMethod">Ver otros métodos</vs-button>
          </div>
        </div>
        <div v-else>
          <div>
            <div class="text-center d-block m-auto">
              <h1 class="text-center mb-5 mt-10">No pudimos verificar tu identidad</h1>
              <p class="d-block m-auto">No pudimos verificar tu identidad. Te invitamos a ponerte en contacto con el creador del documento para que vuelvas a intentar, si lo prefieres, prueba con un método alternativo de firma.</p>
              <h3 class="text-center mb-5 mt-5">Firmar documento con otros métodos:</h3>
              <div class="text-center">
                <vs-button class="mt-5 vs-con-loading__container mb-10" color="primary" @click="signMethod">Ver otros métodos</vs-button>

              </div>
            </div>
          </div>
        </div>
      </div>
      <div :style="!isFinished || isSubmitted ? 'display: none;' : ''">
        <navBar/>
        <transition :name="routerTransition">

          <div v-if="$route.meta.breadcrumb || $route.meta.pageTitle" class="router-header flex flex-wrap items-center mb-6">
            <div
              class="content-area__heading"
              :class="{'pr-4 border-0 md:border-r border-solid border-grey-light' : $route.meta.breadcrumb}">
              <h2 class="mb-1 mt-0">Firma autógrafa</h2>
            </div>

            <!-- BREADCRUMB -->
            <vx-breadcrumb class="ml-4 md:block hidden" v-if="$route.meta.breadcrumb" :route="$route" :isRTL="$vs.rtl" />

          </div>
        </transition>

        <div class="text-center d-block m-auto">
          <h1 class="text-center mb-5">Dibuja el trazo de tu firma</h1>
          <p class="d-block m-auto">¡Estás a un paso de completar tu firma! Por favor, dibuja tu firma en el espacio proporcionado. Si necesitas ajustar algo, tienes la opción de borrar o deshacer los cambios.</p>

          <!-- Aviso sobre grabación de video - Solo mostrar si se requiere video -->
          <div v-if="requiresVideoRecording" class="video-notice mb-4">
            <vs-chip v-if="hasCameraAccess" color="primary">
              <vs-avatar>
                <feather-icon icon="VideoIcon" class="stroke-current text-white" svgClasses="w-4 h-4"></feather-icon>
              </vs-avatar>
              Se está grabando un video para verificar tu identidad
            </vs-chip>
            <vs-chip v-else color="danger">
              <vs-avatar>
                <feather-icon icon="AlertTriangleIcon" class="stroke-current text-white" svgClasses="w-4 h-4"></feather-icon>
              </vs-avatar>
              No se pudo acceder a la cámara.
              <span class="ml-2 cursor-pointer underline" @click="showCameraModal = true">Ver instrucciones</span>
            </vs-chip>
          </div>
        </div>

        <!-- Contenedor principal para cámara y firma -->
        <div class="signature-container" :class="{ 'no-video': !requiresVideoRecording }">
          <!-- Video de cámara de fondo (solo si se requiere) -->
          <video
            v-if="requiresVideoRecording"
            ref="cameraBackground"
            autoplay
            muted
            playsinline
            class="camera-background"
          ></video>

          <!-- Área de firma -->
          <div class="signature-box" ref="signatureBox" :class="{ 'signature-box-no-video': !requiresVideoRecording }">
            <div class="signature-wrapper">
              <!-- Texto "Firma aquí" -->
              <div v-if="showSignHereText" class="sign-here-overlay">
                <span>Firma aquí</span>
              </div>

              <vueSignature
                v-if="onMobile"
                ref="signature"
                :sigOption="option"
                :w="'350px'"
                :h="'200px'"
                :disabled="disabled"
                :clear-on-resize="true"
                class="signature-pad"
                :class="{ 'signature-pad-no-video': !requiresVideoRecording }"
              >
              </vueSignature>
              <vueSignature
                v-if="!onMobile"
                ref="signature"
                :sigOption="option"
                :w="'500px'"
                :h="'250px'"
                :disabled="disabled"
                :clear-on-resize="true"
                class="signature-pad"
                :class="{ 'signature-pad-no-video': !requiresVideoRecording }"
              >
              </vueSignature>
            </div>
          </div>

          <!-- Indicador de grabación (solo si se está grabando) -->
          <div class="recording-indicator" v-if="requiresVideoRecording && isRecording">
            <span class="recording-dot"></span>
            <span class="recording-text">Grabando</span>
          </div>
        </div>

        <div class="d-between">
          <vs-button type="border" class="mt-5 vs-con-loading__container" @click="undo">
            Deshacer
            <feather-icon icon="RotateCcwIcon" class="top-3 text-primary" svgClasses="w-4 h-4"></feather-icon>
          </vs-button>
          <vs-button class="mt-5 vs-con-loading__container" @click="save" color="warning" :disabled="requiresVideoRecording && cameraError">
            Firmar
            <feather-icon icon="Edit3Icon" class="top-3 text-primary" svgClasses="w-4 h-4 text-white"></feather-icon>
          </vs-button>
        </div>

      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "@/assets/scss/vuexy/_variables.scss";

h1, .h1 {
  font-size: 35px;
}

h2 {
  font-size: 20px;
  margin-top: 15px;
}

.d-between{
  -webkit-box-pack: justify !important;
  -ms-flex-pack: justify !important;
  justify-content: space-between !important;
  display: flex;
  max-width: 500px;
  margin: 0 auto;
  padding-bottom: 30px;
}

.isActive{
  span{
    color: #fff!important;
  }
}

// Estilos para el contenedor principal
.signature-container {
  position: relative;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  background-color: transparent;
  overflow: hidden;
  border-radius: 8px;
}

// Estilo para la cámara de fondo
.camera-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
  transform: scaleX(-1); // Espejo horizontal para la cámara frontal
}

// Contenedor del área de firma
.signature-box {
  position: relative;
  z-index: 2;
  align-items: center;
  border-radius: 4px;
  display: block;
  margin: 0 auto;
  background-color: transparent;

  &.signature-box-no-video {
    background-color: white;
  }
}

.signature-wrapper {
  margin: 50px auto;
  border-radius: 8px;
  box-shadow: 0px 0px 40px 5px rgba(0,0,0,0.2);
  position: relative;
  width: 500px;
  height:250px;
}

.signature-pad {
  background-color: rgba(255, 255, 255, 0) !important;
  position: absolute;
  transform: translateY(-50%);
  top: 50%;

  &.signature-pad-no-video {
    background-color: rgba(255, 255, 255, 1) !important;
  }
}

.signature-box-mobile {
  max-width: 350px!important;
  max-height: 200px!important;

  .signature-wrapper {
    width: 350px !important;
    height: 200px !important;
  }
}

// Indicador de grabación
.recording-indicator {
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.6);
  padding: 4px 8px;
  border-radius: 4px;
  z-index: 3;
}

.recording-dot {
  width: 10px;
  height: 10px;
  background-color: red;
  border-radius: 50%;
  margin-right: 5px;
  animation: blink 1s infinite;
}

.recording-text {
  color: white;
  font-size: 12px;
}

p {
  max-width: 850px;
  font-size: 18px;
  margin-bottom: 30px!important;
}

.video-notice {
  display: inline-block;
  margin-top: -15px;
  margin-bottom: 15px;
}

// Estilos para el texto "Firma aquí"
.sign-here-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3;
  pointer-events: none; /* Para que no interfiera con el trazado */

  span {
    font-size: 28px;
    color: rgba(0, 0, 0, 0.4);
    font-weight: 500;
    padding: 8px 20px;
    background-color: rgba(255, 255, 255, 0.5);
    border-radius: 4px;
    animation: fadeIn 0.5s ease-in;
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes blink {
  0% { opacity: 1; }
  50% { opacity: 0.3; }
  100% { opacity: 1; }
}

@media (max-width: 768px) {
  .btn-d{
    display: none;
  }
  h1, .h1 {
    font-size: 25px;
  }
  p{
    font-size: 14px;
  }
  .d-between{
    button{
      margin-bottom: 15px;
    }
  }

  .signature-wrapper {
    width: 350px;
    height: 200px;
  }
}

.primary{
  color: $primary;
  font-weight: 700;
}

.sing-recomendations{
  max-width: 790px;
  display: block;
  margin: 0 auto;
  .primary{
    color: $primary;
    font-weight: 700;
  }
  ul {
    list-style-type: disc;
    padding-left: 20px;
    margin: 10px 0 20px;

    li {
      margin-bottom: 5px;
      line-height: 1.5;
    }
  }
  h2{
    font-size: 20px;
    margin-top: 15px;
    span{
      color: $primary!important;
      font-weight: 700;
    }
  }
}
</style>
